import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Lightning, ArrowClockwise, CheckCircle, Warning } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "medium" });
  } catch { return iso; }
}

export default function RazorpayWebhooksPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/razorpay/webhooks?limit=50");
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-muted-foreground" data-testid="admin-rzp-loading">Loading webhooks…</div>;

  return (
    <div className="space-y-6" data-testid="admin-razorpay-panel">
      <div className="card-flat p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-serif text-2xl mb-1 inline-flex items-center gap-2">
              <Lightning size={18} weight="duotone" className="text-[hsl(var(--secondary))]" /> Razorpay webhooks
            </h3>
            <p className="text-sm text-muted-foreground">
              Server-side payment confirmations from Razorpay. This is the primary capture path —
              independent of browser JS handler reliability.
            </p>
          </div>
          <button onClick={load} disabled={loading} className="btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="admin-rzp-refresh">
            <ArrowClockwise size={12} weight="bold" /> {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
        <div className="mt-4 flex items-center gap-2 text-sm">
          {data.webhook_configured ? (
            <span className="inline-flex items-center gap-1.5 text-[hsl(var(--secondary))]" data-testid="admin-rzp-config-ok">
              <CheckCircle size={14} weight="duotone" /> Webhook secret configured
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[hsl(var(--primary))]" data-testid="admin-rzp-config-missing">
              <Warning size={14} weight="duotone" /> RAZORPAY_WEBHOOK_SECRET not set — webhooks will be rejected with 503
            </span>
          )}
        </div>
      </div>

      <div className="card-flat p-5">
        <div className="eyebrow mb-3">Setup checklist</div>
        <ol className="text-sm space-y-1.5 list-decimal pl-5 text-muted-foreground">
          <li>Open <span className="font-mono text-xs">Razorpay Dashboard → Settings → Webhooks → Add new</span></li>
          <li>URL: <span className="font-mono text-xs">https://&lt;your-domain&gt;/api/razorpay/webhook</span></li>
          <li>Secret: generate a random string, save it to <span className="font-mono text-xs">backend/.env</span> as <span className="font-mono text-xs">RAZORPAY_WEBHOOK_SECRET</span>, paste same string into the Razorpay form</li>
          <li>Events: subscribe to <span className="font-mono text-xs">payment.captured</span> and <span className="font-mono text-xs">payment.failed</span></li>
          <li>Restart backend (<span className="font-mono text-xs">sudo supervisorctl restart backend</span>)</li>
        </ol>
      </div>

      <div className="card-flat p-0 overflow-hidden">
        <div className="px-5 py-3 border-b hairline">
          <h3 className="font-serif text-xl">Recent events ({data.count})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="px-5 py-2">Received</th>
                <th className="px-5 py-2">Event</th>
                <th className="px-5 py-2">Order ID</th>
                <th className="px-5 py-2">Payment ID</th>
                <th className="px-5 py-2">Amount</th>
                <th className="px-5 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-muted-foreground" data-testid="admin-rzp-empty">No webhook events yet.</td></tr>
              )}
              {data.items.map((e, i) => (
                <tr key={e.event_id || `${e.received_at}-${i}`} className="border-t hairline" data-testid={`admin-rzp-row-${e.event_id || i}`}>
                  <td className="px-5 py-2 text-xs text-muted-foreground">{fmt(e.received_at)}</td>
                  <td className="px-5 py-2 font-mono text-xs">{e.event}</td>
                  <td className="px-5 py-2 font-mono text-xs">{e.order_id || "—"}</td>
                  <td className="px-5 py-2 font-mono text-xs">{e.payment_id || "—"}</td>
                  <td className="px-5 py-2 num-metric text-xs">{e.amount ? `₹${(e.amount / 100).toFixed(0)}` : "—"}</td>
                  <td className="px-5 py-2 text-xs">{e.status || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
